/**
 * BUILD YOUR X1 — configurator data.
 *
 * Every price, option, image slot and Shopify variant used by the builder lives
 * here. Paste a Shopify cart link or bare variant ID into BUILDER_LINKS and the
 * matching option can check out. While a link is empty, the builder still works
 * visually, but checkout for that configuration shows a "not connected yet"
 * notice instead of sending anyone to a wrong or broken product.
 */

export type LensId = 'clear' | 'amber' | 'smoke'
export type ProtectionId = 'basic' | 'oneYear' | 'lifetime'
export type BuilderStepId = 'lens' | 'lensSystem' | 'case' | 'care' | 'rx' | 'protection' | 'review'

export const BUILDER_ID = 'build-your-x1'

export const builderPrices = {
  essential: 29.95,
  fullLensSystem: 9.95,
  hardCase: 14.95,
  careKit: 7.95,
  rxInsert: 14.95,
  oneYearProtection: 9.95,
  lifetimeProtection: 19.95,
  complete: 49.95,
  /** Lifetime upgrade on a Complete System (routes to the existing $69.90 product). */
  completeLifetime: 19.95,
  hardCaseDownsell: 9.95,
  /** Future A/B test price. Disabled at launch. */
  hardCaseDownsellTest: 4.95,
} as const

/** Keep the $4.95 hard-case downsell OFF until an A/B test is set up. */
export const HARD_CASE_DOWNSELL_TEST_ENABLED = false

/**
 * Shopify links for the builder products. Paste the full cart link or the variant ID.
 * Do not reuse another product's link: an empty value is safe, a wrong one is not.
 */
export const BUILDER_LINKS = {
  /** GRITVUE X1 ESSENTIAL · $29.95 · Clear variant · free U.S. shipping */
  essentialClear: 'https://shop.gritvuegear.com/cart/48898680881245:1', // SKU GV-X1-ESS-CLEAR
  /** GRITVUE X1 ESSENTIAL · $29.95 · Amber variant · free U.S. shipping */
  essentialAmber: 'https://shop.gritvuegear.com/cart/48898680914013:1', // SKU GV-X1-ESS-AMBER
  /** GRITVUE X1 ESSENTIAL · $29.95 · Smoke variant · free U.S. shipping */
  essentialSmoke: 'https://shop.gritvuegear.com/cart/48898680946781:1', // SKU GV-X1-ESS-SMOKE
  /** FULL LENS SYSTEM UPGRADE · $9.95 · the two lenses the customer did not pick */
  fullLensSystem: 'https://shop.gritvuegear.com/cart/48898693300317:1', // SKU GV-X1-LENS-FULL
  /** HARD PROTECTIVE CASE · $14.95 */
  hardCase: 'https://shop.gritvuegear.com/cart/48898705227869:1', // SKU GV-X1-CASE
  /** HARD PROTECTIVE CASE · checkout downsell · $9.95 (single X1 orders only) */
  hardCaseDownsell: '',
  /** HARD PROTECTIVE CASE · A/B test downsell · $4.95 · DISABLED at launch */
  hardCaseDownsellTest: '',
  /** ANTI-FOG CARE KIT · $7.95 · mini anti-fog bottle + microfiber cloth */
  careKit: 'https://shop.gritvuegear.com/cart/48898707914845:1', // SKU GV-X1-CARE
  /** GRITVUE RX CAPABILITY INSERT · $14.95 each · insert only, no prescription lenses */
  rxInsert: '',
  /** 1-YEAR BREAKAGE PROTECTION · $9.95 · X1 Essential only */
  oneYearProtection: '',
  /** LIFETIME BREAKAGE PROTECTION · $19.95 · X1 Essential only */
  lifetimeProtection: '',
}

export type BuilderLinkKey = keyof typeof BUILDER_LINKS

export interface LensOption {
  id: LensId
  name: string
  blurb: string
  swatch: string
  /** Tint drawn over the lens area of the base photo until real per-lens photos are supplied. */
  tint: { color: string; blend: 'screen' | 'multiply' | 'normal' }
  /** Filter used on the loose spare-lens photo. */
  spareFilter: string
  link: BuilderLinkKey
}

export const lensOptions: LensOption[] = [
  {
    id: 'clear',
    name: 'Clear',
    blurb: 'Maximum natural visibility.',
    swatch: '#d9dde2',
    tint: { color: 'rgba(0,0,0,0)', blend: 'normal' },
    spareFilter: 'none',
    link: 'essentialClear',
  },
  {
    id: 'amber',
    name: 'Amber',
    blurb: 'Contrast-focused option.',
    swatch: '#e8a317',
    tint: { color: 'rgba(236,150,18,0.55)', blend: 'screen' },
    spareFilter: 'sepia(1) saturate(6) hue-rotate(-12deg) brightness(1.15)',
    link: 'essentialAmber',
  },
  {
    id: 'smoke',
    name: 'Smoke',
    blurb: 'Bright-light / glare-focused option.',
    swatch: '#3b3d42',
    tint: { color: 'rgba(6,6,8,0.62)', blend: 'multiply' },
    spareFilter: 'grayscale(1) brightness(0.55) contrast(1.2)',
    link: 'essentialSmoke',
  },
]

export const lensById = (id: LensId) => lensOptions.find((lens) => lens.id === id) ?? lensOptions[0]

/** The two lenses the Full Lens System adds, given the primary lens. */
export const remainingLenses = (primary: LensId) => lensOptions.filter((lens) => lens.id !== primary)

export interface BuilderStep {
  id: BuilderStepId
  label: string
  optional: boolean
}

export const builderSteps: BuilderStep[] = [
  { id: 'lens', label: 'Lens', optional: false },
  { id: 'lensSystem', label: 'Lens system', optional: true },
  { id: 'case', label: 'Case', optional: true },
  { id: 'care', label: 'Care', optional: true },
  { id: 'rx', label: 'RX', optional: true },
  { id: 'protection', label: 'Protection', optional: true },
  { id: 'review', label: 'Review', optional: false },
]

export interface ProtectionOption {
  id: ProtectionId
  name: string
  price: number
  body: string
}

export const essentialProtection: ProtectionOption[] = [
  {
    id: 'basic',
    name: '30-Day Basic Coverage',
    price: 0,
    body: 'Included with every X1 Essential.',
  },
  {
    id: 'oneYear',
    name: '1-Year Breakage Protection',
    price: builderPrices.oneYearProtection,
    body: 'Break it during your covered year? We replace the covered X1 under the protection terms.',
  },
  {
    id: 'lifetime',
    name: 'Lifetime Breakage Protection',
    price: builderPrices.lifetimeProtection,
    body: 'Break it while you remain the original covered owner? We replace the covered X1 under the Lifetime terms.',
  },
]

export const completeProtection: ProtectionOption[] = [
  {
    id: 'basic',
    name: '1-Year Replacement Coverage',
    price: 0,
    body: 'Included with the X1 Complete System.',
  },
  {
    id: 'lifetime',
    name: 'Lifetime Replacement Warranty',
    price: builderPrices.completeLifetime,
    body: 'Break it. We replace it. For as long as you, the original purchaser, own the X1.',
  },
]

/**
 * What the Complete System includes, valued at the builder's own à-la-carte prices.
 * Items with no separate price are shown as "Included" and are not counted in the value.
 */
export const completeUnlocks: { label: string; value: number | null }[] = [
  { label: 'GritVue X1 with your lens', value: builderPrices.essential },
  { label: 'The other 2 lenses (Full Lens System)', value: builderPrices.fullLensSystem },
  { label: 'Hard protective case', value: builderPrices.hardCase },
  { label: '1-Year Replacement Coverage', value: builderPrices.oneYearProtection },
  { label: 'Protective pouch and cleaning cloth', value: null },
  { label: 'Quick Start Guide', value: null },
]

/** Sum of the priced items above ($64.80). */
export const completeSeparateValue =
  Math.round(completeUnlocks.reduce((sum, item) => sum + (item.value ?? 0), 0) * 100) / 100

export const essentialIncludes = [
  'GritVue X1 illuminated work-glasses frame',
  '1 lens of your choice',
  'USB-C charging cable',
]

/**
 * Visual asset slots. The stage composes these layers live.
 * Swap any URL for real product photography shot on a pure black (or transparent)
 * background, all at the SAME angle, camera position, scale and lighting, so the
 * layers line up. Per-lens photos replace the tint overlay automatically.
 *
 * Real GritVue assets still needed:
 *  - X1 Clear / Amber / Smoke, isolated (x1ByLens)
 *  - Full Lens System: X1 plus Clear / Amber / Smoke (spareLens is tinted until then)
 *  - Hard protective case (hardCase)
 *  - GritVue mini anti-fog / lens cleaner + microfiber cloth (careKit)
 *  - RX insert isolated (rxInsert) and installed behind the main lens (rxInstalled)
 *  - Complete System composition and universal GritVue packaging
 */
export const builderAssets = {
  /** Optional: X1 with the RX insert installed behind the main lens, same framing as x1Base. */
  rxInstalled: '',
  /** X1 frame with a CLEAR lens, three-quarter view, pure black background, 4:3. */
  x1Base: 'https://cdn.magicpatterns.com/patterns/generated-images/acdde853-1e12-4ffd-90aa-29dcdf32cb8f.jpg',
  /** Optional real photos of the X1 with each lens fitted. Leave empty to use the tint overlay. */
  x1ByLens: {
    clear: 'https://cdn.magicpatterns.com/uploads/tjqPj7mbyYSNiWjcV31mdH/BB95F6D2-BFBB-48D0-AF01-A27A7E95B95F.png',
    amber: 'https://cdn.magicpatterns.com/uploads/4EyhyyLxYL6PP8r4wg6VzK/6296E825-336D-4D44-9A2D-2B91F76D9165.png',
    smoke: 'https://cdn.magicpatterns.com/uploads/jfZ3V8iLtS6ZzZyZKm9LL5/A1D1F501-7C3F-47B4-BC48-AF2FDAF8AEE9.png',
  } as Record<LensId, string>,
  /** Real studio shot of all three lenses side by side (left to right: Smoke, Amber, Clear). */
  fullLensSystem: 'https://cdn.magicpatterns.com/uploads/bYpwPUF42TD7tnd9AHLqny/ECEEFA84-3F37-4C11-8BE7-E678E4EB4742.png',
  /** A single loose spare lens, pure black background, 1:1. Tinted per lens. */
  spareLens: 'https://cdn.magicpatterns.com/patterns/generated-images/afc83e8b-3a8c-433c-9ff4-f7d030c212e0.jpg',
  /** Real GritVue X1 Hard Protective Case photo (from the Shopify product). */
  hardCase: 'https://cdn.shopify.com/s/files/1/0651/3544/4061/files/9FA0C683-E6CB-48C1-BD42-66D85670AA9C.png?v=1790470412',
  /** Real GritVue X1 Anti-Fog Care Kit photo (from the Shopify product). */
  careKit: 'https://cdn.shopify.com/s/files/1/0651/3544/4061/files/054F7FC5-CC68-4744-9F2E-1A5294AA9F91.png?v=1790470585',
  rxInsert: 'https://cdn.magicpatterns.com/patterns/generated-images/33f7e195-5a26-4765-980e-79ac1101eb62.jpg',
}

/** Lens outlines on the base photo (percent of width/height), used for the tint overlay. */
export const lensClipPaths = [
  'polygon(32.5% 43.3%, 39.2% 41.3%, 53.3% 42%, 63.3% 43.9%, 64.3% 48.9%, 62.5% 57.8%, 58.3% 64.4%, 50% 65.8%, 39.2% 65%, 33.8% 62.2%, 32.1% 53.3%)',
  'polygon(13.3% 35.6%, 19.2% 36.7%, 25% 41.1%, 26.3% 47.8%, 25% 57.8%, 22.5% 61.7%, 16.7% 60.9%, 14.2% 55.6%, 12.9% 46.7%)',
]
